/* Primary depth gallery, synchronized with Shopify's media and variant state. */
(() => {
  class MediaDepth extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        this.native.classList.add('loc-media-depth-native');
        this.enablePrimary();
        this.observer.observe(this.native, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
        this.sync();
        return;
      }
      this.native = this.previousElementSibling;
      this.thumbs = [...this.native.querySelectorAll('.slideshow-controls__thumbnail')];
      if (this.thumbs.length < 2) return;
      const images = this.thumbs.map(thumb => thumb.querySelector('img') ||
        new DOMParser().parseFromString(thumb.textContent, 'text/html').querySelector('img'));
      if (images.some(image => !image)) { this.remove(); return; }
      this.ready = true;
      this.setAttribute('role', 'region');
      const zh = document.documentElement.lang.startsWith('zh');
      this.setAttribute('aria-label', zh ? '商品图片选择' : 'Product image picker');
      this.stage = document.createElement('div');
      this.stage.className = 'loc-media-depth__stage';
      this.cards = images.map((image, i) => {
        const card = document.createElement('button');
        card.type = 'button'; card.className = 'loc-media-depth__card';
        card.setAttribute('aria-label', this.thumbs[i].getAttribute('aria-label'));
        const preview = image.cloneNode(true);
        preview.removeAttribute('srcset'); preview.removeAttribute('sizes');
        const src = new URL(preview.getAttribute('src'), location.href);
        src.searchParams.set('width', '1280');
        preview.src = src.href; preview.loading = i === 0 ? 'eager' : 'lazy'; preview.draggable = false;
        card.append(preview);
        card.addEventListener('click', event => {
          if (this.dragged) return;
          if (i === this.index) this.openMedia(i, event);
          else this.choose(i);
        });
        this.stage.append(card);
        return card;
      });
      this.append(this.stage);
      for (const direction of [-1, 1]) {
        const arrow = document.createElement('button');
        arrow.type = 'button'; arrow.className = 'loc-media-depth__arrow';
        arrow.dataset.direction = direction;
        arrow.setAttribute('aria-label', direction < 0 ? (zh ? '上一张图片' : 'Previous image') : (zh ? '下一张图片' : 'Next image'));
        arrow.innerHTML = direction < 0
          ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 6-6 6 6 6"/></svg>'
          : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m10 6 6 6-6 6"/></svg>';
        arrow.addEventListener('click', () => this.choose(this.index + direction));
        this.append(arrow);
      }
      const dots = document.createElement('div'); dots.className = 'loc-media-depth__dots';
      this.dots = this.thumbs.map((thumb, i) => {
        const dot = document.createElement('button'); dot.type = 'button';
        dot.setAttribute('aria-label', thumb.getAttribute('aria-label'));
        dot.append(document.createElement('span'));
        dot.addEventListener('click', () => this.choose(i));
        dots.append(dot); return dot;
      });
      this.append(dots);
      this.addEventListener('keydown', event => {
        this.dragged = false;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.choose(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
        }
      });
      this.stage.addEventListener('pointerdown', event => {
        this.dragged = false; this.start = { x: event.clientX, y: event.clientY };
      });
      this.stage.addEventListener('pointerup', event => {
        if (!this.start) return;
        const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
        this.start = null;
        if (Math.abs(dx) > 35 && Math.abs(dx) > Math.abs(dy)) {
          this.dragged = true; this.choose(this.index + (dx < 0 ? 1 : -1));
        }
      });
      this.stage.addEventListener('pointercancel', () => { this.start = null; });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.native, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
      this.native.classList.add('loc-media-depth-native');
      this.enablePrimary();
      this.sync();
    }
    enablePrimary() {
      this.slideshow = this.native.closest('slideshow-component');
      this.slideshow.classList.add('loc-media-depth-main');
      this.container = this.slideshow.querySelector(':scope > slideshow-container');
      if (this.container) this.container.inert = true;
    }
    async openMedia(index, event) {
      const gallery = this.closest('media-gallery') || this.closest('.product-information__media');
      const zoom = gallery?.querySelector('zoom-dialog');
      if (zoom) {
        await customElements.whenDefined('zoom-dialog');
        zoom.open(index, event);
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.native?.classList.remove('loc-media-depth-native');
      this.slideshow?.classList.remove('loc-media-depth-main');
      if (this.container) this.container.inert = false;
    }
    choose(index) {
      const next = (index + this.thumbs.length) % this.thumbs.length;
      this.thumbs[next].click();
      this.sync();
    }
    sync() {
      const selected = this.thumbs.findIndex(thumb => thumb.getAttribute('aria-selected') === 'true');
      this.index = Math.max(0, selected);
      this.cards.forEach((card, i) => {
        let offset = i - this.index;
        if (offset > this.cards.length / 2) offset -= this.cards.length;
        if (offset < -this.cards.length / 2) offset += this.cards.length;
        const distance = Math.abs(offset);
        card.style.setProperty('--offset', offset);
        card.style.setProperty('--distance', distance);
        card.style.zIndex = 10 - distance;
        card.style.visibility = distance > 2 ? 'hidden' : 'visible';
        card.tabIndex = distance > 2 ? -1 : 0;
        card.setAttribute('aria-pressed', String(i === this.index));
        this.dots[i].setAttribute('aria-current', String(i === this.index));
      });
    }
  }
  customElements.define('loc-media-depth', MediaDepth);
  let queued = false;
  function mount() {
    queued = false;
    document.querySelectorAll('.product-information__media slideshow-controls[thumbnails]').forEach(controls => {
      if (controls.nextElementSibling?.matches('loc-media-depth')) return;
      const thumbs = controls.querySelectorAll('.slideshow-controls__thumbnail');
      if (thumbs.length < 2 || !controls.querySelector('img')) return;
      controls.after(document.createElement('loc-media-depth'));
    });
  }
  new MutationObserver(() => {
    if (!queued) { queued = true; requestAnimationFrame(mount); }
  }).observe(document.documentElement, { childList: true, subtree: true });
  mount();
})();
